import { msg } from 'gt-react';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { SD849StatusFilterSchema } from '@erp/rpc';
import { ProductModule } from '@erp/shared-types';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../../route';
import { lineResultsSearchSchema } from '../849s/lineResultsFilter';

const ShipAndDebit849ResponsesPage = lazyRouteComponent(
  () => import('../849s/ShipAndDebit849ResponsesPage'),
  'ShipAndDebit849ResponsesPage',
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/849-responses',
  staticData: {
    app: {
      nav: {
        label: msg('849 Responses', {
          context: 'sidebar nav label, Ship and Debit section',
        }),
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SD849Response },
        ],
      },
    },
  },
  component: ShipAndDebit849ResponsesPage,
  loader: () => ({
    crumb: msg('849 Responses', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SD849Response,
    });
  },
  validateSearch: z.object({
    page: z.number().int().min(0).optional(),
    pageSize: z.number().int().optional().catch(undefined),
    suppliers: z.array(z.string()).optional(),
    statuses: lineResultsSearchSchema,
    // The claim status — what we're doing about the claim, as opposed to
    // `statuses` above (the supplier's rollup). `none` selects the rows nobody
    // has triaged; see SD849StatusFilterSchema in the contract.
    claimStatuses: z.array(SD849StatusFilterSchema).optional().catch(undefined),
    sourceTypes: z
      .array(z.enum(['email', 'edi']))
      .optional()
      .catch(undefined),
    processedAtFrom: z.string().optional(),
    processedAtTo: z.string().optional(),
    // "Last updated" — when the supplier last contacted us about the claim,
    // which is a different question from when we created the row, so it gets
    // its own range rather than sharing `processedAt`'s.
    lastUpdatedFrom: z.string().optional(),
    lastUpdatedTo: z.string().optional(),
    // `.catch(undefined)` so a hand-edited or stale URL falls back to the
    // list's default order instead of failing route parsing and blanking the
    // page.
    sortBy: z
      .enum(['processedAt', 'lastUpdatedAt'])
      .optional()
      .catch(undefined),
    sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
    batchNumber: z.array(z.string()).optional(),
    // Mirrors the RPC contract's shape so a hand-edited URL is rejected at route
    // parsing instead of being client-accepted then server-rejected.
    vdmNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
    divisions: z.array(z.string()).optional(),
  }),
});
